import { getStoredSessionToken } from "./appleAuth";
import type {
  CalendarMonthResponse,
  DayDetailResponse,
  ReadingPackDetailDto,
  ReadingSection,
} from "./types";

async function authHeader(): Promise<HeadersInit> {
  const token = getStoredSessionToken();
  if (!token) throw new Error("Sign in required.");
  return { Authorization: `Bearer ${token}` };
}

async function readingsFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(await authHeader());
  if (init?.body) headers.set("Content-Type", "application/json");
  return fetch(`/api/readings/${path}`, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchCalendarMonth(month: string): Promise<CalendarMonthResponse> {
  const res = await readingsFetch(`calendar?month=${encodeURIComponent(month)}`);
  return readJson(res);
}

export async function fetchDayDetail(dateKey: string): Promise<DayDetailResponse> {
  const res = await readingsFetch(`day/${encodeURIComponent(dateKey)}`);
  return readJson(res);
}

export type UpdatePackContentResult = {
  recordName: string;
  status: string;
  updatedAt: string;
  pack: ReadingPackDetailDto;
};

export async function updatePackContent(input: {
  recordName: string;
  sections: ReadingSection[];
  liturgicalTitle?: string | null;
}): Promise<UpdatePackContentResult> {
  const res = await readingsFetch("packs/update", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const date = new Date(Date.UTC(y!, m! - 1 + delta, 1));
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${yy}-${mm}`;
}

export function currentUtcMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}
